import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DataFeedService } from '../../../shared/api/datafeed.service';
import { AppService } from '../../../store/app.service';
import { IClient } from '../../../core/models/clients.models';
import { CampaignsApiService } from '../../campaigns/campaigns.apiservice';
import { ClientsApiService } from '../../clients/clients.apiservice';
import { takeUntil } from 'rxjs/operators';
import { ReplaySubject } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { ROLES } from '../../../core/helpers/constants';
import { UsersApiService } from '../../users/users.apiservice';
import { ICampaign } from '../../../core/models/campaigns.model';

@Component({
  selector: 'app-tech-request',
  templateUrl: './tech-request.component.html',
  styleUrls: ['./tech-request.component.scss']
})
export class TechRequestComponent implements OnInit, OnDestroy {
  @Input() campaignId?: string;
  @Input() clientId?: string;
  techForm: FormGroup;
  allClients: IClient[] = [];
  campaignsData: Array<any> = [];
  allCampaigns: Array<any> = [];
  techUsers: Array<any> = [];
  currentUserEmail: string = '';

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private fb: FormBuilder,
    private dataFeedService: DataFeedService,
    private campaignService: CampaignsApiService,
    private appService: AppService,
    public activeModal: NgbActiveModal,
    private http: HttpClient,
    private usersApiService: UsersApiService,
    private clientService: ClientsApiService
  ) {
    this.techForm = this.fb.group({
      FormType: ['Tech Request'],
      AssignTo: ['', Validators.required],
      clientId: [''],
      campaignId: [''],
      Title: ['', Validators.required],
      Request: ['', Validators.required],
      ResponseEmail: ['', [Validators.required, Validators.email]],
    });
    this.dataFeedService.getAllUsersWithRole(ROLES.TechServices.Code)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response.good) {
          this.techUsers = response.data.map((i: any) => ({ ...i, fullName: i.firstName + ' ' + i.lastName }));
        }
      });
  }

  ngOnInit() {
    // Capture current user email for use in the form/payload
    const userId = this.appService.getActiveUser$().value?.id || '';
    this.usersApiService.getUserDetails(userId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response && response.data.email) {
          this.currentUserEmail = response.data.email;
          this.techForm.get('ResponseEmail')?.setValue(this.currentUserEmail);
        }
      });

    if (this.campaignId) {
      this.campaignService.getCampaignDetails(this.campaignId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          this.allCampaigns = [response.data];
          this.campaignsData = [response.data];
          this.clientService.getClientDetails(response.data.clientId)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((c: any) => {
              this.allClients = [c.data];
              this.techForm.get('clientId')?.setValue(c.data.id);
            });
        });
      this.techForm.get('campaignId')?.setValue(this.campaignId);
    } else if (this.clientId) {
      this.campaignService.getCampaignsByClient(this.clientId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          this.campaignsData = response.data.map((campaign: any) => {
            if (campaign.stageId === 8) {
              campaign.statusColor = 'gray';
            }
            return campaign;
          }).sort((a: ICampaign, b: ICampaign) => a.name.localeCompare(b.name));
        });
      this.clientService.getClientDetails(this.clientId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          this.allClients = [response.data];
        });
      this.techForm.get('clientId')?.setValue(this.clientId);
    } else {
      this.dataFeedService.getAllClients()
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          if (response.good) {
            this.allClients = response.data.map((i: any) => ({ ...i, fullName: i.firstName + ' ' + i.lastName }));
          }
        });
      this.appService.getAllCampaigns$()
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          this.allCampaigns = response.data.filter((campaign: any) => {
            return !campaign.isDeleted && campaign.stage !== 'Inactive';
          });
          this.filterCampaignsByClient();
        });
      this.techForm.get('clientId')?.valueChanges
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => {
          this.filterCampaignsByClient();
          this.techForm.get('campaignId')?.setValue('');
        });
      this.appService.fetchAllCampaigns({});
    }
  }

  filterCampaignsByClient() {
    const clientId = this.techForm.get('clientId')?.value;
    if (clientId) {
      this.campaignsData = this.allCampaigns.filter((c: any) => c.clientId === clientId);
    } else {
      this.campaignsData = [...this.allCampaigns];
    }
  }

  onSubmit() {
    if (this.techForm.valid) {
      const formData = this.techForm.value;
      this.techForm.disable();
      this.http.post(`${environment.apiBaseUrl}/techrequest`, formData)
        .subscribe({
          next: (response) => {
            console.log('Form submitted successfully:', response);
            this.activeModal.close(response);
          },
          error: (error) => {
            console.error('Error submitting form:', error);
            this.techForm.enable();
          }
        });
    }
  }

  close() {
    this.activeModal.dismiss();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
